import React, { useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { GridApi, ColDef, GridOptions, ValueFormatterParams, CellClassParams, RowClassParams } from 'ag-grid-enterprise';
import { CellClickedEvent, CellValueChangedEvent, ModelUpdatedEvent } from 'ag-grid-community';

import BtnCopyRenderer from './BtnCopyRenderer';
import MachineRecordsTrashRenderer from './MachineRecordsTrashRenderer';
import InputDatetimeCellEditor from './InputDatetimeCellEditor';
import { orgRound } from '../../utils/utils'

// CSS
import './MachineRecordsGrid.css';
import 'ag-grid-enterprise/styles/ag-grid.css';
import 'ag-grid-enterprise/styles/ag-theme-balham.css';
import { forEach } from 'lodash';

interface GridProps {
  onGridReadyParent: (params: { api: any }) => void;
}

const Grid: React.FC<GridProps> = ({onGridReadyParent}) => {

  const [gridApi, setGridApi] = useState<GridApi | null>(null);

  // グリッドの初期化
  const onGridReady = (params: { api: GridApi }) => {
    setGridApi(params.api);
    onGridReadyParent(params);
  }

  const onModelUpdated = (event: ModelUpdatedEvent) => {
    // グリッドのデータが追加されたときに、エラーチェックを行う
    event.api.forEachNode((node) => {
      checkRowError(node.data);
    });
    event.api.redrawRows();
  }

  // 編集した行かつエラーがある行かどうか判定する
  const isEditErrorRow = (data: any) => {
    if (!data) {
      return false;
    }
    let isEdit = false;
    forEach(data, (value, key) => {
      if (key.startsWith("is_edit_")) {
        isEdit = isEdit || value;
      }
    });
    isEdit = isEdit || data.is_add;
    if (!isEdit) {
      // 編集していないため対象外
      return false;
    }
    if (data.error_message) {
      return true;
    }
    return false;
  }

  // エラー表示フォーマッター
  const errorFormatter = (params: ValueFormatterParams) => {
    if (isEditErrorRow(params.data)) {
      return "★"
    }
    return "";
  }

  // 工場フォーマッター
  const plantNameFormatter = (params: ValueFormatterParams) => {
    if (params.data) {
      return params.data.plant_code + ":" + params.data.plant_name;
    }
    return "";
  }

  // 加工フォーマッター
  const procNameFormatter = (params: ValueFormatterParams) => {
    if (params.data) {
      return params.data.proc_code + ":" + params.data.proc_name;
    }
    return "";
  }

  // 作業機械フォーマッター
  const machNameFormatter = (params: ValueFormatterParams) => {
    if (params.data) {
      return params.data.mach_code + ":" + params.data.mach_name;
    }
    return "";
  }

  // 日時のセルフォーマッター
  const datetimeCellValueFormatter = (params: ValueFormatterParams) => {
    if (params.value) {
      try {
        const date = new Date(params.value)
        const year = (date.getFullYear() + '').slice(-2);
        const month = ("0" + (date.getMonth() + 1)).slice(-2); // 月は0から始まるため、1を加える
        const day = ("0" + date.getDate()).slice(-2);
        const hours = ("0" + date.getHours()).slice(-2);
        const minutes = ("0" + date.getMinutes()).slice(-2);
        const seconds = ("0" + date.getSeconds()).slice(-2);
        return `${year}/${month}/${day} ${hours}:${minutes}:${seconds}`;
      } catch (e) {
        console.log(e);
      }
    }
    return "";
  }

  // IDフォーマッター
  const machIdFormatter = (params: ValueFormatterParams) => {
    if (params.data && params.data.id > 0) {
      return params.data.id;
    }
    return "";
  }

  // コピー行の仮ID
  const [copyId, setCopyId] = useState<number>(-1);

  // コピーボタン押下時の処理
  const onCopyCellClicked = (params: CellClickedEvent) => {
    const result = window.confirm("コピーしますか？");
    if (!result) {
      // キャンセル
      return;
    }
    let data = JSON.parse(JSON.stringify(params.data));
    data.id = copyId;
    data.is_add = true;
    setCopyId(copyId - 1);
    if (params.rowIndex !== null && params.rowIndex !== undefined) {
      params.api.applyTransaction({ add: [data], addIndex: params.rowIndex + 1 });
    }
  }

  // 削除ボタン押下時の処理
  const onDeleteCellClicked = (params: CellClickedEvent) => {
    if (!params.data.is_add) {
      // コピー行でないので削除不可
      return;
    }
    const result = window.confirm("削除しますか？");
    if (!result) {
      // キャンセル
      return;
    }
    params.api.applyTransaction({ remove: [params.data] });
  }

  // セルの値変更時の処理
  const onCellValueChanged = (event: CellValueChangedEvent) => {
    try {
      if (event.colDef.field === "start_time" || event.colDef.field === "end_time") {
        let inputValue = event.newValue;
        if (!inputValue) {
          // 未入力の場合
          event.node.setDataValue(event.colDef.field, "");
          event.node.setDataValue("duration", 0);
          return;
        }
        
        if (inputValue.length === "24/01/01T00:00:00".length) {
          // 年が2桁の場合は、変更後の値に年を追加
          inputValue = (new Date()).getFullYear().toString().slice(0, 2) + inputValue;
        }
        // 年月日と時刻の間にTがない場合は、追加する
        inputValue = inputValue.replace(" ", "T");
        // 年月日の区切り文字がスラッシュの場合は、ハイフンに変換する
        inputValue = inputValue.replace(/\//g, "-");

        if (Number.isNaN((new Date(inputValue)).getTime())) {
          // 日時の入力が不正な場合
          event.node.setDataValue(event.colDef.field, "");
          event.node.setDataValue("duration", 0);
          return;
        }
        // 日付のフォーマットを修正している場合は、設定しなおす。
        if (event.newValue != inputValue) {
          event.node.setDataValue(event.colDef.field, inputValue);
        }

        // 作業時間を更新する。
        let start_time = null;
        let end_time = null;
        if (event.colDef.field === "start_time") {
          start_time = inputValue;
          end_time = event.data.end_time;
        } else {
          start_time = event.data.start_time;
          end_time = inputValue;
        }
        event.node.setDataValue("duration", calDuration(start_time, end_time) ?? 0);
      }
    } catch (e) {
      console.log(e);
    } finally {
      updateCellEditFlag(event); // 編集フラグを設定
      checkRowError(event.node.data);  // エラーチェック
      event.api.redrawRows({ rowNodes: [event.node] }); // 行を再描画
    }
  }

  // 作業時間の計算
  const calDuration = (start_time: string|null, end_time: string|null): number|null => {
    if (!start_time || !end_time) {
      return null;
    }
    const start_date = new Date(start_time);
    const end_date = new Date(end_time);
    const work_time = (end_date.getTime() - start_date.getTime()) / 1000 / 60 / 60;
    const duration = Math.round(work_time * 100) / 100;
    if (duration < 0) {
      return 0.0;
    }
    return orgRound(duration, 100);
  }

  // セルの編集フラグの更新
  const updateCellEditFlag = (event: CellValueChangedEvent) => {
    if (event.colDef.field === "start_time") {
      event.data.is_edit_start_time = true;
    }
    if (event.colDef.field === "end_time") {
      event.data.is_edit_end_time = true;
    }
    if (event.colDef.field === "duration") {
      event.data.is_edit_duration = true;
    }
  }

  // 値変更時のRowデータのエラーチェック
  const checkRowError = (rowData: any) => {
    // 変更行のエラーフラグをクリアする
    forEach(rowData, (value, key) => {
      if (key.startsWith("is_error_")) {
        rowData[key] = false;
      }
    });
    rowData["error_message"] = null;

    // 開始時刻
    if (!rowData.start_time) {
      rowData["is_error_start_time"] = true;
      rowData["error_message"] = rowData["error_message"] || "開始時刻が未入力です。";
    } else if (new Date(rowData.start_time) > new Date(rowData.end_time)) {
      // 開始時刻・終了時刻が反転している場合は、エラーとする。
      rowData["is_error_start_time"] = true;
      rowData["error_message"] = rowData["error_message"] || "開始時刻と終了時刻が不正です。";
    }

    // 終了時刻
    if (!rowData.end_time) {
      rowData["is_error_end_time"] = true;
      rowData["error_message"] = rowData["error_message"] || "終了時刻が未入力です。";
    } else if (new Date(rowData.start_time) > new Date(rowData.end_time)) {
      // 開始時刻・終了時刻が反転している場合は、エラーとする。
      rowData["is_error_end_time"] = true;
      rowData["error_message"] = rowData["error_message"] || "開始時刻と終了時刻が不正です。";
    }
  }

  // 行のCSSクラスの設定
  const rowClassRules = {
    'machinerecords-row-add': (params: RowClassParams) => {
      return params.data.is_add === true
    },
  }

  // セルのCSSクラスの設定
  const cellClassRules = {
    'machinerecords-edit': (params: CellClassParams) => {
        if (params.colDef.field === 'start_time') {
          return params.data.is_edit_start_time
        }
        if (params.colDef.field === 'end_time') {
          return params.data.is_edit_end_time
        }
        if (params.colDef.field === 'duration') {
          return params.data.is_edit_duration
        }
        return false;
    },
    'machinerecords-error': (params: CellClassParams) => {
      if (params.colDef.field === 'start_time') {
        return params.data.is_error_start_time
      }
      if (params.colDef.field === 'end_time') {
        return params.data.is_error_end_time
      }
      if (params.colDef.field === 'duration') {
        return params.data.is_error_duration
      }
      return false;
    },
    'machinerecords-edit-error-message-edit': (params: CellClassParams) => {
      if (params.colDef.field === 'error_message') {
        return isEditErrorRow(params.data);
      }
      return false;
    }
  }
  
  const columns: ColDef[] = [
    { headerName: '', field: '', minWidth:38, maxWidth:38, cellRenderer: BtnCopyRenderer, onCellClicked: onCopyCellClicked, cellStyle: { 'padding': '0 4px' }},
    { headerName: '', field: '', minWidth:38, maxWidth:38, cellRenderer: MachineRecordsTrashRenderer, onCellClicked:onDeleteCellClicked, cellStyle: { 'padding': '0 4px' }},
    { headerName: 'NG', field: '', minWidth:38, maxWidth:38, valueFormatter: errorFormatter, cellClass: 'machinerecords-edit-error-flag', },
    { headerName: '工場', field: 'plant_code', valueFormatter: plantNameFormatter, },
    { headerName: '加工', field: 'proc_code', valueFormatter: procNameFormatter, },
    { headerName: '機械作業', field: 'mach_code', valueFormatter: machNameFormatter, },
    { headerName: '開始時刻', field: 'start_time', minWidth:130, maxWidth:130, editable: true, valueFormatter: datetimeCellValueFormatter, cellEditor: InputDatetimeCellEditor, headerClass:"machrecords-edit-header", cellClassRules: cellClassRules},
    { headerName: '終了時刻', field: 'end_time', minWidth:130, maxWidth:130, editable: true, valueFormatter: datetimeCellValueFormatter, cellEditor: InputDatetimeCellEditor, headerClass:"machrecords-edit-header", cellClassRules: cellClassRules},
    { headerName: '時間', field: 'duration' , cellClassRules: cellClassRules},
    { headerName: 'ID', field: 'id', valueFormatter: machIdFormatter, },
    { headerName: '', field: 'error_message', minWidth:250, editable: false, cellClassRules: cellClassRules,},
  ];

  const defaultColDef = {
    resizable: true,
    floatingFilter: false,
    sortable: false,
    enableMenu:false,
    enableSorting: false,
    enableFilter: false,
    pinSubMenu:false,
    rowSelection:'single',
  };

  // グリッドオプション
  const gridOptions: GridOptions = {
    defaultColDef: defaultColDef,
    columnDefs: columns,
    rowClassRules: rowClassRules,
    rowHeight:35,
    //
    getRowId: (params) => {
      return params.data.id;
    },
    onGridReady: onGridReady,
    onCellValueChanged: onCellValueChanged,
    onModelUpdated: onModelUpdated,
    // suppressClipboardPaste: true, // クリップボードの貼り付けを無効にする
  };

  return (
    // 描画するReact要素
    <div className="ag-theme-balham">
      <AgGridReact
        {...gridOptions}
        //rowData={rowData}
        domLayout={"normal"}  // テーブルの高さを自動調整
      />
    </div>
  );
};

export default Grid;